import React from "react";
import styled from "styled-components";

import { MemoryIcon } from "../../../../components";
import { Modal, ModalButton, ModalFooter } from "../../../../components/common/Modal";
import { COLORS } from "../../../../constants";
import { decodeBlankLines, encodeBlankLines } from "../../../WebDashboardViewer/markdownBlankLines";
import { RichTextEditor } from "../../../WebDashboardViewer/RichTextEditor";
import { useMemory } from "../../../WebDashboardViewer/useMemory";

interface MemoryModalProps {
  visible: boolean;
  onClose: () => void;
}

const Description = styled.p`
  font-size: 14px;
  color: ${COLORS.black45};
  margin: 0 0 12px;
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 240px;
  resize: none;
  border: 1px solid ${COLORS.black15};
  border-radius: 8px;
  padding: 10px;
  font-size: 14px;
  line-height: 20px;
  outline: none;

  &:focus {
    border-color: ${COLORS.coeblue4};
  }
`;

/* Renders the same read-only Lexical editor as the in-viewer drawer so blank-line
   semantics match exactly (ReactMarkdown couldn't render the drawer's persisted
   format: commonmark collapses the extra blank lines that mark intentional gaps,
   and lazily continues list items into following paragraphs). The workspace
   document doesn't load Tailwind, so the Lexical theme classes are inert here and
   element selectors below provide the styling. */
const ReadView = styled.div`
  height: 240px;
  overflow-y: auto;
  border-radius: 8px;
  background: ${COLORS.black4};
  padding: 12px;
  font-size: 14px;
  line-height: 20px;
  color: ${COLORS.black65};

  [contenteditable] {
    outline: none;
  }

  h1,
  h2,
  h3,
  h4 {
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    line-height: 20px;
    color: ${COLORS.black65};
    margin: 16px 0 0;
  }

  h1:first-child,
  h2:first-child,
  h3:first-child,
  h4:first-child {
    margin-top: 0;
  }

  ul,
  ol {
    margin: 0;
    padding-left: 18px;
  }

  ul {
    list-style: disc;
  }

  li,
  p {
    margin: 0;
  }

  a {
    color: ${COLORS.coeblue4};
    text-decoration: underline;
  }
`;

// Title row with the memory glyph (matches the in-viewer modal + Figma).
const TitleRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

// Single right-aligned button (view mode). ModalFooter uses space-between, which
// left-aligns a lone child; this pushes Edit to the right per the design.
const FooterRight = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

const FooterLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CharCount = styled.span<{ $over: boolean }>`
  font-size: 12px;
  color: ${({ $over }) => ($over ? COLORS.red : COLORS.black45)};
`;

/**
 * Workspace-level Memory modal (Dashboards listing → "Memory" button). Mirrors the
 * in-viewer memory dialog, but built on the shared antd/styled Modal because the
 * main workspace document does NOT load Tailwind (that's CDN-injected only inside
 * the dashboard iframe). Same data hooks, same full-blob last-write-wins contract.
 */
export const MemoryModal: React.FC<MemoryModalProps> = ({ visible, onClose }) => {
  const {
    savedMemory,
    isSaving,
    maxChars,
    draft,
    setDraft,
    isDirty,
    isOverLimit,
    showEditor,
    startEditing,
    save,
  } = useMemory(visible);

  return (
    <Modal
      title={
        <TitleRow>
          <MemoryIcon width="20px" height="20px" />
          Memory
        </TitleRow>
      }
      visible={visible}
      onClose={onClose}
      width={640}
    >
      <Description>
        Memory applies across all your dashboards and nothing is saved without your approval.
      </Description>

      {showEditor ? (
        // The textarea shows/edits the decoded (editor-convention) form — one
        // blank line = one intentional gap — while `draft` holds the encoded
        // storage form, keeping char counts and dirtiness against what's saved.
        <TextArea
          value={decodeBlankLines(draft)}
          onChange={e => setDraft(encodeBlankLines(e.target.value))}
          placeholder='Nothing saved yet. Ask the AI to "remember" a preference, or write one here.'
          data-sentry-mask
        />
      ) : (
        <ReadView data-sentry-mask>
          <RichTextEditor
            key={savedMemory}
            initialMarkdown={savedMemory}
            editable={false}
            showToolbar={false}
            preserveNewLines
            namespace="WorkspaceMemoryReader"
            contentClassName=""
          />
        </ReadView>
      )}

      {showEditor ? (
        <ModalFooter>
          <FooterLeft>
            <CharCount $over={isOverLimit}>
              {draft.length} / {maxChars}
            </CharCount>
          </FooterLeft>
          <ModalButton onClick={save} disabled={!isDirty || isOverLimit || isSaving}>
            Save
          </ModalButton>
        </ModalFooter>
      ) : (
        <FooterRight>
          <ModalButton onClick={startEditing}>Edit</ModalButton>
        </FooterRight>
      )}
    </Modal>
  );
};
